import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import AtomImage from '@s-ui/react-atom-image'
import {inputSizes} from '@s-ui/react-atom-input'
import AtomValidationText, {AtomValidationTextTypes} from '@s-ui/react-atom-validation-text'
import Context from '@s-ui/react-context'
import MoleculeInputField from '@s-ui/react-molecule-input-field'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationBorderSizes,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import ModalBasic, {moleculeModalSizes} from '@adv-mt/mt-modal-basic'
import EyeOffOutline from '@adv-mt/mt-svg-icons/lib/EyeOffOutline'
import EyeOutline from '@adv-mt/mt-svg-icons/lib/EyeOutline'
import LockOutline from '@adv-mt/mt-svg-icons/lib/LockOutline'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const BASE_CLASS = 'mt-ModalChangeUserPassword'

const MIN_LENGTH = 8
const MAX_LENGTH = 20

const NO_OP = () => {}

export default function ModalChangeUserPassword({isOpen, onClose = NO_OP, onSuccess = NO_OP, currentPassword}) {
  const {i18n, domain} = useContext(Context)

  const config = domain.get('config')
  const urlStaticsImages = config.get('URL_STATICS_IMAGES')
  const companyName = config.get('COMPANY_NAME')
  const isMobile = config.get('isMobile')
  const isWiderThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {defaultMatches: !isMobile})
  const imageSrc = `${urlStaticsImages}illustrations/change-password.svg`

  const [newPassword, setNewPassword] = useState('')
  const [fieldError, setFieldError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const validatePassword = value => {
    if (!value || value.length < MIN_LENGTH || value.length > MAX_LENGTH) {
      return i18n.t('USER_PROFILE.EDIT.PASSWORD.FIELD_CONSTRAINTS_ERROR_TEXT')
    }
    return null
  }

  const handleBlur = e => {
    if (!e.target.value) return
    setFieldError(validatePassword(e.target.value))
  }

  const handleChange = e => {
    setNewPassword(e.target.value)
    if (fieldError) setFieldError(null)
    if (submitError) setSubmitError(null)
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const error = validatePassword(newPassword)
    if (error) {
      setFieldError(error)
      return
    }

    setIsLoading(true)
    const [updateError] = await domain
      .get('update_password_user_use_case')
      .execute({password: currentPassword, newPassword})
    setIsLoading(false)

    if (updateError) {
      setSubmitError(i18n.t('MODAL_CHANGE_USER_PASSWORD.ERROR_GENERIC'))
      return
    }

    onSuccess()
  }

  return (
    <ModalBasic isInsetScrollArea isOpen={isOpen} onClose={onClose} size={moleculeModalSizes.MEDIUM}>
      <div className={BASE_CLASS}>
        <div className={`${BASE_CLASS}-notification`}>
          <MoleculeNotification
            alignItems={moleculeNotificationAlignItems.FLEX_START}
            autoClose={false}
            effect={false}
            roundedCorners={moleculeNotificationBorderSizes.medium}
            showCloseButton={false}
            showLeftIconMobile
            type={moleculeNotificationTypes.success}
            variation={moleculeNotificationVariations.positive}
          >
            <div>
              <strong>{i18n.t('MODAL_CHANGE_USER_PASSWORD.NOTIFICATION_TITLE')}</strong>
            </div>
            <div>{i18n.t('MODAL_CHANGE_USER_PASSWORD.NOTIFICATION_DESCRIPTION')}</div>
          </MoleculeNotification>
        </div>
        <div className={`${BASE_CLASS}-imageWrapper`}>
          <AtomImage className={`${BASE_CLASS}-image`} src={imageSrc} alt="" />
        </div>
        <TitleBasic
          as={titleBasicTags.h2}
          size={titleBasicSizes.m}
          title={i18n.t('MODAL_CHANGE_USER_PASSWORD.TITLE')}
        />
        <p className={`${BASE_CLASS}-description`}>{i18n.t('MODAL_CHANGE_USER_PASSWORD.DESCRIPTION', {companyName})}</p>
        <form className={`${BASE_CLASS}-form`} onSubmit={handleSubmit}>
          <div className={`${BASE_CLASS}-field`}>
            <MoleculeInputField
              autoComplete="new-password"
              errorText={fieldError || undefined}
              helpText={i18n.t('USER_PROFILE.EDIT.PASSWORD.FIELD_CONSTRAINTS_ERROR_TEXT')}
              leftIcon={<LockOutline size="medium" />}
              maxLength={MAX_LENGTH}
              minLength={MIN_LENGTH}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder={i18n.t('MODAL_CHANGE_USER_PASSWORD.INPUT_PLACEHOLDER')}
              pwHideLabel={<EyeOffOutline size="medium" />}
              pwShowLabel={<EyeOutline size="medium" />}
              required
              size={inputSizes.LARGE}
              type="sui-password"
              value={newPassword}
            />
          </div>
          {submitError && <AtomValidationText type={AtomValidationTextTypes.ERROR} text={submitError} />}
          <div className={`${BASE_CLASS}-footer`}>
            <AtomButton
              design={atomButtonDesigns.SOLID}
              fullWidth={!isWiderThanMobile}
              isLoading={isLoading}
              shape={atomButtonShapes.CIRCULAR}
              size={atomButtonSizes.LARGE}
              type="submit"
            >
              {i18n.t('MODAL_CHANGE_USER_PASSWORD.CTA')}
            </AtomButton>
          </div>
        </form>
      </div>
    </ModalBasic>
  )
}

ModalChangeUserPassword.displayName = 'ModalChangeUserPassword'
ModalChangeUserPassword.propTypes = {
  currentPassword: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
}
